import useTableColumnBuilder from "hooks/useTableColumnBuilder";
import { ISupplyOrder } from "domain/Inventory/Models/ISupplyOrder";
import { SBTranslate } from "i18n";
import { SBBox, SBIcon, SBTooltip } from "modules/Commons";

export const useSupplyOrderHeaders = () => {
	const { add, build, integerFormatter, currencyFormatter, dateFormatter } =
		useTableColumnBuilder<ISupplyOrder>();

	add({
		id: () => "inventory",
		name: " ",
		hideToggle: true,
		headers: [
			{
				id: (c) => (
					<SBBox style={{ cursor: "pointer" }}>
						<SBIcon icon="inventory" hoverFillColor="primary" />
					</SBBox>
				),
				csvName: SBTranslate("inventory"),
				align: "center",
				csvHide: true,
				width: 1,
				key: "inventory",
				name: "",
				fixed: true,
			},
		],
	});

	add({
		id: () => "order",
		name: "order",
		headers: [
			{
				id: (c) => c.guid ?? "",
				key: "guid",
				name: SBTranslate("code"),
				csvName: SBTranslate("supplyOrderCSVNames.guid"),
				align: "left",
				noWrap: true,
			},
			{
				id: (c) => c.requisitionClientID ?? "---",
				key: "requisitionClientID",
				name: SBTranslate("requisitionClientID"),
				csvName: SBTranslate("supplyOrderCSVNames.requisitionClientID"),
				align: "left",
				noWrap: true,
			},
			{
				id: (c) => c.orderClientID ?? "---",
				key: "orderClientID",
				name: SBTranslate("orderClientID"),
				csvName: SBTranslate("supplyOrderCSVNames.orderClientID"),
				align: "left",
				noWrap: true,
			},
		],
	});

	add({
		id: () => "product",
		name: "product",
		headers: [
			{
				id: (c) => c.productClientID ?? "",
				key: "productClientID",
				name: SBTranslate("code"),
				csvName: SBTranslate("supplyOrderCSVNames.productClientID"),
				align: "left",
				noWrap: true,
			},
			{
				id: (c) => c.productName ?? "",
				key: "productName",
				name: SBTranslate("product"),
				csvName: SBTranslate("supplyOrderCSVNames.productName"),
				align: "left",
				noWrap: true,
			},
		],
	});

	add({
		id: () => "store",
		name: "store",
		headers: [
			{
				id: (c) => c.storeClientID ?? "",
				key: "storeClientID",
				name: SBTranslate("code"),
				csvName: SBTranslate("supplyOrderCSVNames.storeClientID"),
				align: "left",
				noWrap: true,
			},
			{
				id: (c) => c.storeName ?? "",
				key: "storeName",
				name: SBTranslate("store"),
				csvName: SBTranslate("supplyOrderCSVNames.storeName"),
				align: "left",
				noWrap: true,
			},
		],
	});

	add({
		id: () => "supplier",
		name: "supplier",
		headers: [
			{
				id: (c) => c.supplierClientID ?? "",
				key: "supplierClientID",
				name: SBTranslate("code"),
				csvName: SBTranslate("supplyOrderCSVNames.supplierClientID"),
				align: "left",
				noWrap: true,
			},
			{
				id: (c) => c.supplierName ?? "",
				key: "supplierName",
				name: SBTranslate("supplier"),
				csvName: SBTranslate("supplyOrderCSVNames.supplierName"),
				align: "left",
				noWrap: true,
			},
		],
	});

	add({
		id: () => "dates",
		name: "dates",
		headers: [
			{
				id: (c) => c.created ?? "",
				key: "created",
				name: SBTranslate("created"),
				csvName: SBTranslate("supplyOrderCSVNames.created"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.created ?? ""),
			},
			{
				id: (c) => c.ordered ?? "",
				key: "ordered",
				name: SBTranslate("ordered"),
				csvName: SBTranslate("supplyOrderCSVNames.ordered"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.ordered ?? ""),
			},
			{
				id: (c) => c.expected ?? "",
				key: "expected",
				name: SBTranslate("expected"),
				csvName: SBTranslate("supplyOrderCSVNames.expected"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.expected ?? ""),
			},
			{
				id: (c) => c.integrated ?? "",
				key: "integrated",
				name: SBTranslate("integrated"),
				csvName: SBTranslate("supplyOrderCSVNames.integrated"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.integrated ?? ""),
			},
			{
				id: (c) => c.received ?? "",
				key: "received",
				name: SBTranslate("received"),
				csvName: SBTranslate("supplyOrderCSVNames.received"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.received ?? ""),
			},
			{
				id: (c) => c.closed ?? "",
				key: "closed",
				name: SBTranslate("closed"),
				csvName: SBTranslate("supplyOrderCSVNames.closed"),
				align: "left",
				noWrap: true,
				formatter: dateFormatter,
				csvValue: (c) => dateFormatter(c.closed ?? ""),
			},
		],
	});

	add({
		id: () => "values",
		name: "values",
		headers: [
			{
				id: (c) => c.quantity,
				key: "quantity",
				name: SBTranslate("quantity"),
				csvName: SBTranslate("supplyOrderCSVNames.quantity"),
				align: "right",
				noWrap: true,
				formatter: integerFormatter,
			},
			{
				id: (c) => c.receivedQuantity,
				key: "receivedQuantity",
				name: SBTranslate("receivedQuantity"),
				csvName: SBTranslate("supplyOrderCSVNames.receivedQuantity"),
				align: "right",
				noWrap: true,
				formatter: integerFormatter,
			},
			{
				id: (c) => c.cost,
				key: "cost",
				name: SBTranslate("cost"),
				csvName: SBTranslate("supplyOrderCSVNames.cost"),
				align: "right",
				noWrap: true,
				formatter: currencyFormatter,
			},
			{
				id: (c) => c.supplyBrainRecommendation,
				key: "supplyBrainRecommendation",
				name: SBTranslate("supplyBrainRecommendation"),
				csvName: SBTranslate("supplyOrderCSVNames.supplyBrainRecommendation"),
				align: "right",
				noWrap: true,
				formatter: integerFormatter,
			},
		],
	});

	return { headers: build() };
};
